/**
 * Shared upper-section component for portfolio-mode pages (Revenue, P&L).
 *
 * Renders: summary cards, a stacked UK/FR bar chart (with optional forecast),
 * and YTD region sums beneath the chart. All values follow the shared
 * display-currency toggle via usePortfolioPrefsStore.
 */
import React, { useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { formatCompact, REGION_COLORS } from '../lib/chartFormatters';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { convertAmount, currencyLabel } from '../lib/currencyConvert';
import { cn } from '../lib/utils';
import {
  mergeWithForecast,
  ForecastCaveat,
  ForecastBarShape,
} from '../lib/forecastChartUtils';
import { computeYtdSums } from '../lib/portfolioMetricUtils';
import { ForecastTotalsFooter } from './dashboard/ForecastTotalsFooter';

const CURRENCY_SYMBOLS = { GBP: '\u00A3', EUR: '\u20AC', USD: '$', DKK: 'kr' };

const formatAmount = (value, currency = 'GBP') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency + ' ';
  const sign = value < 0 ? '-' : '';
  return `${sign}${sym}${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function SummaryCard(props) {
  const { label, value, icon: Icon, color, sub, change } = props;
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', color)}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-2xl font-bold font-mono', sub)}>{value}</p>
            {change !== null && change !== undefined && (
              <p className={cn(
                'text-xs font-medium mt-0.5',
                change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                {change >= 0 ? '+' : ''}{change.toFixed(0)}% YoY
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Tooltip for "all" tab (stacked UK + FR) */
function StackedTooltip(props) {
  const { active, payload, label, displayCurrency, eurGbpRate, ukLabel, frLabel } = props;
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const cv = (v, from) => convertAmount(v, from, displayCurrency, eurGbpRate);
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {d._ukNative !== 0 && (
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">{ukLabel} ({currencyLabel('GBP', displayCurrency)})</span>
          <span className="font-mono">{formatAmount(cv(d._ukNative, 'GBP'), displayCurrency)}</span>
        </div>
      )}
      {d._frNative !== 0 && (
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">{frLabel} ({currencyLabel('EUR', displayCurrency)})</span>
          <span className="font-mono">{formatAmount(cv(d._frNative, 'EUR'), displayCurrency)}</span>
        </div>
      )}
      <div className="flex justify-between gap-4 border-t mt-1 pt-1 font-semibold">
        <span>Combined ({displayCurrency} approx)</span>
        <span className="font-mono">{formatAmount(cv(d._ukNative, 'GBP') + cv(d._frNative, 'EUR'), displayCurrency)}</span>
      </div>
    </div>
  );
}

/** Tooltip for single-region tab */
function SingleRegionTooltip(props) {
  const { active, payload, label, currency } = props;
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex justify-between gap-4">
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span className="font-mono">{formatAmount(entry.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array} props.cards - SummaryCard definitions: { label, value, icon, color, sub, change }
 * @param {Array} props.monthlySeries - [{ month: 'Jan', ukNative: number, frNative: number }]
 *   ukNative in GBP, frNative in EUR (native currencies)
 * @param {object|null} props.forecastByMonth - { '2026-06': { uk: number, fr: number } } (optional)
 * @param {string} props.title - Chart title prefix (e.g. "Net Booking Revenue Trend")
 * @param {string} props.ukLabel - Legend label for UK bars (e.g. "UK Net")
 * @param {string} props.frLabel - Legend label for FR bars (e.g. "FR Net")
 * @param {string} props.displayCurrency - 'GBP' | 'EUR'
 * @param {number} props.eurGbpRate - EUR_GBP conversion rate
 * @param {number} props.year
 * @param {string} props.portfolioRegion - 'all' | 'uk' | 'fr'
 * @param {boolean} [props.showForecastCaveat] - show forecast caveat text
 */
// Props destructured in body, not params: workaround for Emergent babel-metadata-plugin crash (line 936). Do not convert to function({...}).
export function PortfolioMetricChart(props) {
  const {
    cards,
    monthlySeries,
    forecastByMonth,
    title,
    ukLabel = '🇬🇧 UK',
    frLabel = '🇫🇷 FR',
    displayCurrency,
    eurGbpRate,
    year,
    portfolioRegion,
    showForecastCaveat = false,
  } = props;
  const cv = (value, from) => convertAmount(value, from, displayCurrency, eurGbpRate);
  const nativeRegionCurrency = portfolioRegion === 'fr' ? 'EUR' : 'GBP';

  // Dynamic bar labels reflecting display currency
  const ukBarLabel = displayCurrency === 'GBP'
    ? `${ukLabel} (GBP)`
    : `${ukLabel} (EUR approx)`;
  const frBarLabel = displayCurrency === 'GBP'
    ? `${frLabel} (GBP approx)`
    : `${frLabel} (EUR)`;

  // Build chart data from the normalized monthly series
  const chartDataBase = useMemo(() => {
    if (!monthlySeries?.length) return [];
    const cvM = (v, from) => convertAmount(v, from, displayCurrency, eurGbpRate);

    if (portfolioRegion === 'uk') {
      return monthlySeries.map((m) => ({
        month: m.month,
        revenue: cvM(m.ukNative || 0, 'GBP'),
      }));
    }
    if (portfolioRegion === 'fr') {
      return monthlySeries.map((m) => ({
        month: m.month,
        revenue: cvM(m.frNative || 0, 'EUR'),
      }));
    }
    // All: stacked UK + FR
    return monthlySeries.map((m) => ({
      month: m.month,
      [ukBarLabel]: cvM(m.ukNative || 0, 'GBP'),
      [frBarLabel]: cvM(m.frNative || 0, 'EUR'),
      _ukNative: m.ukNative || 0,
      _frNative: m.frNative || 0,
    }));
  }, [monthlySeries, portfolioRegion, eurGbpRate, displayCurrency, ukBarLabel, frBarLabel]);

  // Merge forecast if available
  const chartData = useMemo(() => {
    if (!forecastByMonth || Object.keys(forecastByMonth).length === 0) return chartDataBase;

    if (portfolioRegion === 'uk') {
      return mergeWithForecast(chartDataBase, forecastByMonth, (fm) => ({
        revenue: fm.uk || fm.revenue_uk_gbp || fm.revenue_gbp || 0,
      }), year);
    }
    if (portfolioRegion === 'fr') {
      return mergeWithForecast(chartDataBase, forecastByMonth, (fm) => ({
        revenue: fm.fr || fm.revenue_fr_gbp || fm.revenue_gbp || 0,
      }), year);
    }
    return mergeWithForecast(chartDataBase, forecastByMonth, (fm) => {
      const uk = fm.uk || fm.revenue_uk_gbp || 0;
      const fr = fm.fr || fm.revenue_fr_gbp || 0;
      return { [ukBarLabel]: uk, [frBarLabel]: fr };
    }, year);
  }, [chartDataBase, forecastByMonth, portfolioRegion, year, ukBarLabel, frBarLabel]);

  // YTD region sums (native currencies summed, then converted)
  const { uk: ytdUk, fr: ytdFr } = useMemo(
    () => computeYtdSums(monthlySeries),
    [monthlySeries]
  );

  const hasChartData = chartData.some((d) =>
    portfolioRegion === 'all' ? (d[ukBarLabel] !== 0 || d[frBarLabel] !== 0) : d.revenue !== 0
  );

  const chartTitle = portfolioRegion === 'all'
    ? `${title} (${displayCurrency} approx) - ${year}`
    : `${title} (${currencyLabel(nativeRegionCurrency, displayCurrency)}) - ${year}`;

  const hasForecast = showForecastCaveat && forecastByMonth && Object.keys(forecastByMonth).length > 0;

  return (
    <>
      {/* Summary Cards */}
      {cards && cards.length > 0 && (
        <div className={cn('grid gap-4', cards.length <= 2 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-4')}>
          {cards.map((card, i) => (
            <SummaryCard key={i} {...card} />
          ))}
        </div>
      )}

      {/* Stacked Bar Chart */}
      {hasChartData && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{chartTitle}</CardTitle>
            <ForecastCaveat show={hasForecast} />
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} tickFormatter={(v) => `${CURRENCY_SYMBOLS[displayCurrency]}${(v / 1000).toFixed(0)}k`} />

                {portfolioRegion === 'all' ? (
                  <>
                    <Tooltip content={<StackedTooltip displayCurrency={displayCurrency} eurGbpRate={eurGbpRate} ukLabel={ukLabel} frLabel={frLabel} />} />
                    <Legend />
                    <Bar dataKey={ukBarLabel} stackId="metric" fill={REGION_COLORS.UK} shape={<ForecastBarShape data={chartData} />}>
                      <LabelList
                        dataKey={ukBarLabel}
                        position="center"
                        formatter={(v) => v >= 1000 ? formatCompact(v, displayCurrency) : ''}
                        style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                      />
                    </Bar>
                    <Bar dataKey={frBarLabel} stackId="metric" fill={REGION_COLORS.FR} radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={chartData} />}>
                      <LabelList
                        dataKey={frBarLabel}
                        position="center"
                        formatter={(v) => v >= 1000 ? formatCompact(v, displayCurrency) : ''}
                        style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                      />
                      <LabelList
                        position="top"
                        content={(props) => {
                          const { x, y, width, index } = props;
                          const row = chartData[index];
                          const total = (row?.[ukBarLabel] || 0) + (row?.[frBarLabel] || 0);
                          if (!total) return null;
                          return (
                            <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill="#1f2937">
                              {formatCompact(total, displayCurrency)}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </>
                ) : (
                  <>
                    <Tooltip content={<SingleRegionTooltip currency={displayCurrency} />} />
                    <Legend />
                    <Bar
                      dataKey="revenue"
                      name={`${portfolioRegion === 'uk' ? ukLabel : frLabel} (${currencyLabel(nativeRegionCurrency, displayCurrency)})`}
                      fill={portfolioRegion === 'uk' ? REGION_COLORS.UK : REGION_COLORS.FR}
                      radius={[4, 4, 0, 0]}
                      shape={<ForecastBarShape data={chartData} />}
                    >
                      <LabelList dataKey="revenue" position="top" formatter={(v) => formatCompact(v, displayCurrency)} style={{ fontSize: 11, fontWeight: 600, fill: '#1f2937' }} />
                    </Bar>
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>

            {/* YTD Region Sums */}
            <div className="flex items-center gap-6 mt-4 pt-3 border-t text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: REGION_COLORS.UK }} />
                <span className="text-muted-foreground">YTD 🇬🇧 UK</span>
                <span className="font-mono font-semibold">{formatAmount(cv(ytdUk, 'GBP'), displayCurrency)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: REGION_COLORS.FR }} />
                <span className="text-muted-foreground">YTD 🇫🇷 France</span>
                <span className="font-mono font-semibold">{formatAmount(cv(ytdFr, 'EUR'), displayCurrency)}</span>
              </div>
              {portfolioRegion === 'all' && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm bg-foreground" />
                  <span className="text-muted-foreground">YTD Combined</span>
                  <span className="font-mono font-semibold">
                    {formatAmount(cv(ytdUk, 'GBP') + cv(ytdFr, 'EUR'), displayCurrency)}
                  </span>
                </div>
              )}
            </div>
            <ForecastTotalsFooter
              data={chartData}
              series={portfolioRegion === 'all'
                ? [
                    { key: ukBarLabel, label: 'UK', color: REGION_COLORS.UK },
                    { key: frBarLabel, label: 'FR', color: REGION_COLORS.FR },
                  ]
                : [{ key: 'revenue', label: portfolioRegion === 'uk' ? 'UK' : 'FR', color: portfolioRegion === 'uk' ? REGION_COLORS.UK : REGION_COLORS.FR }]}
              currency={displayCurrency}
              label={title}
              showCombined={portfolioRegion === 'all'}
            />
          </CardContent>
        </Card>
      )}
    </>
  );
}
